import React, { useEffect, useState, useMemo } from "react";
import DOMPurify from 'dompurify';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  Alert,
  Collapse,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import GavelIcon from "@mui/icons-material/Gavel";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import BuildIcon from "@mui/icons-material/Build";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LockIcon from "@mui/icons-material/Lock";
import SettingsIcon from "@mui/icons-material/Settings";
import InboxIcon from "@mui/icons-material/Inbox";
import EventIcon from "@mui/icons-material/Event";
import api from "../../utils/api";

// Predefined categories matching the app's main sections
const CATEGORIES = [
  { value: "Violations",            label: "Violations",            icon: GavelIcon,               color: "#c0392b" },
  { value: "Architectural Request", label: "Architectural Request", icon: ArchitectureIcon,         color: "#7b3fa0" },
  { value: "Work Orders",           label: "Work Orders",           icon: BuildIcon,                color: "#d4811a" },
  { value: "Members",               label: "Members",               icon: PeopleIcon,               color: "#1a8a5c" },
  { value: "Events",                label: "Events",                icon: EventIcon,                color: "#0e7c7b" },
  { value: "Payments",              label: "Payments",              icon: AccountBalanceWalletIcon, color: "#2f63ad" },
  { value: "Account & Security",    label: "Account & Security",    icon: LockIcon,                 color: "#546e7a" },
  { value: "System",                label: "System",                icon: SettingsIcon,             color: "#8e44ad" },
  { value: "General",               label: "General",               icon: InboxIcon,                color: "#607d8b" },
];

// Maps every known template name → category
// Used as the primary grouping source so templates are bucketed correctly
// even if the `module` field in the DB hasn't been set yet.
const NAME_TO_CATEGORY = {
  // ── Work Orders ──────────────────────────────────────────────────────────
  workorder:               "Work Orders",
  workorder_comment:       "Work Orders",
  workorder_resolved:      "Work Orders",
  workorder_type_change:   "Work Orders",
  workorder_email:         "Work Orders",
  workorder_status_change: "Work Orders",

  // ── Violations ───────────────────────────────────────────────────────────
  violation_assignment:    "Violations",
  violation_status_change: "Violations",
  violation_comment:       "Violations",
  violation_email:         "Violations",
  violation_notice:        "Violations",
  violation_courtesy:      "Violations",

  // ── Architectural Request ─────────────────────────────────────────────────
  architectural_request:                 "Architectural Request",
  architectural_comment:                 "Architectural Request",
  architectural_request_status_change:   "Architectural Request",
  architectural_request_type_change:     "Architectural Request",
  architectural_request_email:           "Architectural Request",

  // ── Account & Security ───────────────────────────────────────────────────
  user_invitation: "Account & Security",
  forgot_password: "Account & Security",
  access_request:  "Account & Security",

  // ── Events ───────────────────────────────────────────────────────────────
  event:            "Events",
  calendar_reminder: "Events",

  // ── General ──────────────────────────────────────────────────────────────
  assignment: "General",
};

// Maps the API's category field → frontend display category
const API_TO_CATEGORY = {
  work_orders:  "Work Orders",
  violations:   "Violations",
  architectural:"Architectural Request",
  users:        "Account & Security",
  events:       "Events",
};

// Resolve the display category for a template.
// Priority: API category field → name-based map → "General"
const resolveCategory = (tpl) => {
  const byApiField = API_TO_CATEGORY[tpl.category];
  if (byApiField) return byApiField;
  const byName = NAME_TO_CATEGORY[tpl.name];
  if (byName) return byName;
  return "General";
};

const getCategoryMeta = (value) =>
  CATEGORIES.find((c) => c.value === value) || CATEGORIES.find((c) => c.value === "General");

const EmailTemplateManager = () => {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [form, setForm] = useState({ subject: "", body: "", category: "General" });
  const [viewMode, setViewMode] = useState("edit");
  const [saving, setSaving] = useState(false);
  const [openCategories, setOpenCategories] = useState({});
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    api
      .get("/email-templates")
      .then((res) => {
        setTemplates(res.data);
        setIsLoading(false);
        // Auto-expand all categories on first load
        const modules = [...new Set((res.data || []).map((t) => t.module || "General"))];
        const initial = {};
        modules.forEach((m) => (initial[m] = true));
        setOpenCategories(initial);
      })
      .catch((err) => {
        console.error("Error loading templates", err);
        setIsLoading(false);
        setSnack({
          open: true,
          message: "Failed to load templates",
          severity: "error",
        });
      });
  }, []);

  // Group templates by resolved category, ordered by CATEGORIES definition
  const grouped = useMemo(() => {
    const map = {};
    (templates || []).forEach((tpl) => {
      const key = resolveCategory(tpl);
      if (!map[key]) map[key] = [];
      map[key].push(tpl);
    });
    // Keep CATEGORIES display order; append any unknown keys alphabetically at the end
    const knownOrder = CATEGORIES.map((c) => c.value).filter((v) => map[v]);
    const unknownOrder = Object.keys(map)
      .filter((k) => !CATEGORIES.find((c) => c.value === k))
      .sort();
    return [...knownOrder, ...unknownOrder].map((key) => [key, map[key]]);
  }, [templates]);

  const handleSelect = (template) => {
    setSelected(template);
    setForm({
      subject: template.subject || "",
      body: template.body || "",
      category: resolveCategory(template),
    });
    setViewMode("edit");
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put(`/email-templates/${selected.id}`, {
        subject: form.subject,
        body: form.body,
        name: selected.name,
        category: form.category,
        is_active: selected.is_active,
      });

      setTemplates((prev) =>
        prev.map((tpl) =>
          tpl.id === selected.id
            ? { ...tpl, subject: form.subject, body: form.body, category: form.category }
            : tpl
        )
      );

      setSelected((prev) =>
        prev ? { ...prev, subject: form.subject, body: form.body, category: form.category } : prev
      );

      setSnack({
        open: true,
        message: "Template saved successfully",
        severity: "success",
      });
    } catch (err) {
      console.error("Failed to save template", err);
      setSnack({
        open: true,
        message: "Failed to save template",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleViewToggle = (_, newValue) => {
    if (newValue !== null) setViewMode(newValue);
  };

  const toggleCategory = (module) => {
    setOpenCategories((prev) => ({ ...prev, [module]: !prev[module] }));
  };

  // ── Styles ────────────────────────────────────────────────────────────────

  const pageCardSx = {
    maxWidth: 1250,
    mx: "auto",
    mt: 1,
    p: { xs: 2, md: 3 },
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const panelCardSx = {
    borderRadius: "16px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
    backgroundColor: "rgba(255,255,255,0.96)",
    overflow: "hidden",
    height: "100%",
  };

  const fieldSx = {
    mt: 1.25,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": { backgroundColor: "#1f4f94", boxShadow: "none" },
  };

  const toggleButtonSx = {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: "10px !important",
    px: 2,
    py: 0.9,
    color: "#2f63ad",
    borderColor: "rgba(47,99,173,0.18)",
    "&.Mui-selected": {
      backgroundColor: "rgba(47,99,173,0.1)",
      color: "#1f4f94",
    },
    "&.Mui-selected:hover": { backgroundColor: "rgba(47,99,173,0.16)" },
  };

  // ── Loading ───────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Box textAlign="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Paper elevation={0} sx={pageCardSx}>
      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
          Email Templates
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c" }}>
          View, edit, and preview email templates used throughout the app.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* ── Left panel: categorised list ─────────────────────────────── */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={panelCardSx}>
            {/* Panel header */}
            <Box
              sx={{
                px: 2,
                py: 2,
                borderBottom: "1px solid rgba(47,99,173,0.08)",
                backgroundColor: "rgba(47,99,173,0.03)",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142" }}>
                Templates
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.25 }}>
                {templates.length} template{templates.length !== 1 ? "s" : ""} across{" "}
                {grouped.length} categor{grouped.length !== 1 ? "ies" : "y"}
              </Typography>
            </Box>

            {/* Categorised list */}
            {grouped.length > 0 ? (
              <Box sx={{ overflowY: "auto", maxHeight: 560 }}>
                {grouped.map(([module, items], groupIdx) => {
                  const meta = getCategoryMeta(module);
                  const accentColor = meta.color;
                  const CategoryIcon = meta.icon;
                  const isOpen = !!openCategories[module];

                  return (
                    <Box key={module}>
                      {/* Category header row */}
                      <ListItemButton
                        onClick={() => toggleCategory(module)}
                        sx={{
                          px: 2,
                          py: 1.1,
                          backgroundColor: `${accentColor}0d`,
                          borderTop: groupIdx > 0 ? "1px solid rgba(0,0,0,0.06)" : "none",
                          "&:hover": { backgroundColor: `${accentColor}1a` },
                        }}
                      >
                        {/* Category icon */}
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: "8px",
                            backgroundColor: `${accentColor}22`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 1.25,
                            flexShrink: 0,
                          }}
                        >
                          <CategoryIcon sx={{ fontSize: 16, color: accentColor }} />
                        </Box>

                        <ListItemText
                          primary={
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.87rem",
                                color: "#223142",
                                lineHeight: 1.2,
                              }}
                            >
                              {module}
                            </Typography>
                          }
                        />

                        <Chip
                          label={items.length}
                          size="small"
                          sx={{
                            mr: 0.75,
                            height: 20,
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            backgroundColor: `${accentColor}1a`,
                            color: accentColor,
                            "& .MuiChip-label": { px: 1 },
                          }}
                        />

                        {isOpen ? (
                          <ExpandMoreIcon sx={{ fontSize: 18, color: "#8a94a6" }} />
                        ) : (
                          <ChevronRightIcon sx={{ fontSize: 18, color: "#8a94a6" }} />
                        )}
                      </ListItemButton>

                      {/* Collapsible template items */}
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <List disablePadding>
                          {items.map((tpl, idx) => (
                            <React.Fragment key={tpl.id}>
                              <ListItemButton
                                selected={selected?.id === tpl.id}
                                onClick={() => handleSelect(tpl)}
                                sx={{
                                  pl: 4,
                                  pr: 2,
                                  py: 1.25,
                                  alignItems: "flex-start",
                                  borderLeft: `3px solid ${
                                    selected?.id === tpl.id ? accentColor : "transparent"
                                  }`,
                                  "&.Mui-selected": {
                                    backgroundColor: `${accentColor}12`,
                                  },
                                  "&.Mui-selected:hover": {
                                    backgroundColor: `${accentColor}1e`,
                                  },
                                  transition: "border-left-color 0.15s",
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 32, mt: 0.2 }}>
                                  <EmailIcon
                                    fontSize="small"
                                    sx={{
                                      color:
                                        selected?.id === tpl.id ? accentColor : "#b0bac6",
                                      fontSize: 17,
                                    }}
                                  />
                                </ListItemIcon>

                                <ListItemText
                                  primary={
                                    <Typography
                                      sx={{
                                        fontWeight: selected?.id === tpl.id ? 700 : 500,
                                        fontSize: "0.875rem",
                                        color:
                                          selected?.id === tpl.id ? "#223142" : "#3c4a5e",
                                        lineHeight: 1.3,
                                      }}
                                    >
                                      {tpl.name}
                                    </Typography>
                                  }
                                />
                              </ListItemButton>
                              {idx < items.length - 1 && (
                                <Divider sx={{ ml: 4, opacity: 0.5 }} />
                              )}
                            </React.Fragment>
                          ))}
                        </List>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ px: 2, py: 3 }}>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  No templates found.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* ── Right panel: editor / preview ─────────────────────────────── */}
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ ...panelCardSx, p: { xs: 2, md: 3 } }}>
            {selected ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                    mb: 2.5,
                  }}
                >
                  <Box>
                    {(() => {
                      const meta = getCategoryMeta(form.category);
                      const SelIcon = meta.icon;
                      return (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                          <SelIcon sx={{ fontSize: 15, color: meta.color }} />
                          <Typography variant="caption" sx={{ fontWeight: 700, color: meta.color, fontSize: "0.75rem" }}>
                            {meta.label}
                          </Typography>
                        </Box>
                      );
                    })()}
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: "#223142", mb: 0.25 }}
                    >
                      {selected.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      Edit subject, body, and category, then save.
                    </Typography>
                  </Box>

                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewToggle}
                    aria-label="View Mode"
                    sx={{
                      gap: 1,
                      flexWrap: "wrap",
                      "& .MuiToggleButtonGroup-grouped": {
                        borderRadius: "10px !important",
                        border: "1px solid rgba(47,99,173,0.18) !important",
                      },
                    }}
                  >
                    <ToggleButton value="edit" aria-label="Edit" sx={toggleButtonSx}>
                      <EditIcon sx={{ mr: 1, fontSize: 18 }} />
                      Edit
                    </ToggleButton>
                    <ToggleButton value="preview" aria-label="Preview" sx={toggleButtonSx}>
                      <VisibilityIcon sx={{ mr: 1, fontSize: 18 }} />
                      Preview
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* Category selector */}
                <FormControl fullWidth sx={{ mt: 1.25 }}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={form.category}
                    label="Category"
                    onChange={handleChange}
                    sx={{
                      borderRadius: "10px",
                      backgroundColor: "rgba(255,255,255,0.96)",
                    }}
                  >
                    {CATEGORIES.map((cat) => {
                      const CatIcon = cat.icon;
                      return (
                        <MenuItem key={cat.value} value={cat.value}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                            <Box
                              sx={{
                                width: 26,
                                height: 26,
                                borderRadius: "7px",
                                backgroundColor: `${cat.color}1a`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <CatIcon sx={{ fontSize: 15, color: cat.color }} />
                            </Box>
                            <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#223142" }}>
                              {cat.label}
                            </Typography>
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Subject"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  sx={fieldSx}
                />

                {viewMode === "edit" ? (
                  <TextField
                    fullWidth
                    label="Body (HTML)"
                    name="body"
                    value={form.body}
                    onChange={handleChange}
                    multiline
                    minRows={14}
                    sx={{
                      ...fieldSx,
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "rgba(255,255,255,0.96)",
                        borderRadius: "10px",
                        alignItems: "flex-start",
                      },
                    }}
                  />
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      mt: 1.5,
                      p: 2.5,
                      minHeight: 360,
                      borderRadius: "12px",
                      backgroundColor: "#fff",
                      border: "1px solid rgba(47,99,173,0.12)",
                      boxShadow: "inset 0 1px 2px rgba(0,0,0,0.03)",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "#6b778c", mb: 1.5, fontWeight: 600 }}
                    >
                      Preview
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box
                      sx={{
                        "& img": { maxWidth: "100%" },
                        "& table": { maxWidth: "100%" },
                        "& p": { marginTop: 0 },
                      }}
                    >
                      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(form.body || '') }} />
                    </Box>
                  </Paper>
                )}

                <Box sx={{ mt: 2.5, display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={<SaveIcon />}
                    sx={primaryButtonSx}
                  >
                    {saving ? "Saving..." : "Save Template"}
                  </Button>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  minHeight: 420,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  px: 2,
                }}
              >
                <Box>
                  <EmailIcon sx={{ fontSize: 42, color: "rgba(47,99,173,0.45)", mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
                    No template selected
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b778c" }}>
                    Select a template from the left to view and edit it.
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default EmailTemplateManager;
